import { useState } from "react";
import type { Playlist } from "../api/types";
import { backend, usePlayer } from "../app/services";
import { formatClock } from "../lib/time";
import { useLoad } from "../lib/useLoad";
import { materialize, segmentsOfPlaylist } from "../playback/blocks";

/** The account's playlists from the server — stations included — to play back. */
export function LibraryPage() {
  const playlists = useLoad(() => backend.playlists());
  const [selectedID, setSelectedID] = useState<string | null>(null);
  const selected = playlists.data?.find((playlist) => playlist.id === selectedID) ?? null;

  if (selected) return <PlaylistView playlist={selected} onBack={() => setSelectedID(null)} />;

  return (
    <div className="library">
      <div className="row between">
        <h2>Library</h2>
        <button type="button" className="quiet" onClick={playlists.reload}>
          Refresh
        </button>
      </div>
      {playlists.error && <p className="error">{playlists.error}</p>}
      {!playlists.data && !playlists.error && <p className="muted">Loading…</p>}
      <ul className="playlist-list">
        {playlists.data?.map((playlist) => (
          <li key={playlist.id}>
            <button type="button" onClick={() => setSelectedID(playlist.id)}>
              <Cover playlist={playlist} />
              <span className="grow">
                <strong>{playlist.title}</strong>
                <span className="muted">
                  {new Date(playlist.created_at).toLocaleString(undefined, {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  {playlist.radio_mode ? " · station" : ""}
                </span>
              </span>
              <span className="muted">{formatClock(playlist.duration_estimate)}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Cover({ playlist }: { playlist: Playlist }) {
  return playlist.artwork_url ? (
    <img src={playlist.artwork_url} alt="" className="cover" />
  ) : (
    <div className="cover placeholder" />
  );
}

function PlaylistView({ playlist, onBack }: { playlist: Playlist; onBack: () => void }) {
  const { engine, station, resolveTrack } = usePlayer();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState(false);

  async function play(fromPosition?: number) {
    setError(null);
    setLoading(true);
    try {
      const { blocks } = await materialize(segmentsOfPlaylist(playlist.segments), resolveTrack);
      if (blocks.length === 0) {
        setError("None of these tracks play in your Apple Music storefront.");
        return;
      }
      station.leave();
      const start =
        fromPosition === undefined
          ? 0
          : Math.max(
              0,
              blocks.findIndex((block) => (block.position ?? -1) >= fromPosition),
            );
      await engine.load({ id: playlist.id, title: playlist.title, blocks, isLive: false }, start);
    } catch {
      setError("This playlist couldn't be played.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="playlist-view">
      <button type="button" className="quiet" onClick={onBack}>
        ← Library
      </button>
      <div className="row">
        <Cover playlist={playlist} />
        <div className="grow">
          <h2>{playlist.title}</h2>
          {playlist.description && <p className="muted">{playlist.description}</p>}
        </div>
        <button type="button" className="primary" disabled={isLoading} onClick={() => void play()}>
          {isLoading ? "Loading…" : "Play"}
        </button>
      </div>
      {error && <p className="error">{error}</p>}
      <ol className="segments">
        {playlist.segments.map((segment) => (
          <li key={segment.id}>
            <button type="button" className="segment" onClick={() => void play(segment.position)}>
              {segment.type === "track" ? (
                <>
                  <span className="kind">♪</span>
                  <span className="grow">
                    <strong>{segment.title}</strong>
                    <span className="muted"> · {segment.artist}</span>
                  </span>
                </>
              ) : (
                <>
                  <span className="kind">◉</span>
                  <span className="grow script-preview">{segment.script || segment.title}</span>
                </>
              )}
              <span className="muted">{formatClock(segment.duration)}</span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}
